import { memo, useEffect, useRef } from 'react';
import './AmbientEffects.css';

export interface AmbientEffectsProps {
  isMyTurn: boolean;
  isLowTime: boolean;
}

const PARTICLES_MIN_WIDTH = 769;

/**
 * Frame al secondo delle particelle. NON e' la fluidita' dell'app: e' la cadenza di un
 * pulviscolo che si muove di 0,3px per frame.
 *
 * MISURATO (`scripts/measure-drag.mjs`, trascinamento a 1440x900 dsf2, CPU dell'intero
 * albero di processi): a 60fps questo canvas da solo valeva il **34%** della CPU spesa
 * durante il gioco — piu' della scacchiera, dei pezzi e dei materiali messi insieme. Il
 * motivo non e' il disegno (trenta cerchietti) ma il canvas a tutto viewport: ogni frame
 * e' un `clearRect` che sporca l'intera superficie, e quindi un caricamento di texture da
 * qualche megabyte verso il compositore. Ridurre la sola cadenza divide quel conto.
 *
 * Lo SPOSTAMENTO E' COMPENSATO dal tempo trascorso, quindi la velocita' apparente non
 * cambia: le particelle percorrono lo stesso spazio al secondo, con passi piu' radi. A
 * 0,3px/frame di partenza il passo a 24fps resta sotto il pixel e mezzo, cioe' sotto la
 * soglia a cui un pulviscolo a opacita' 0,05-0,2 mostra scatti.
 */
const PARTICLES_FPS = 24;

interface Particle {
  x: number;
  y: number;
  r: number;
  dx: number;
  dy: number;
  o: number;
}

export const AmbientEffects = memo<AmbientEffectsProps>(({ isMyTurn, isLowTime }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Body class manager: low-time has priority over your-turn
  useEffect(() => {
    const body = document.body;
    if (isLowTime) {
      body.classList.add('low-time-glow');
      body.classList.remove('your-turn');
    } else if (isMyTurn) {
      body.classList.add('your-turn');
      body.classList.remove('low-time-glow');
    } else {
      body.classList.remove('your-turn', 'low-time-glow');
    }
    return () => {
      body.classList.remove('your-turn', 'low-time-glow');
    };
  }, [isMyTurn, isLowTime]);

  // Particles canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || window.innerWidth < PARTICLES_MIN_WIDTH) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particles: Particle[] = [];
    let raf = 0;

    const init = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      particles = [];
      for (let i = 0; i < 30; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 1.5 + 0.5,
          dx: (Math.random() - 0.5) * 0.3,
          dy: (Math.random() - 0.5) * 0.15 - 0.1,
          o: Math.random() * 0.3 + 0.1,
        });
      }
    };

    const intervallo = 1000 / PARTICLES_FPS;
    let ultimo = 0;

    const draw = (ora: number) => {
      raf = requestAnimationFrame(draw);
      // Si continua a stare dentro rAF — cosi' l'animazione si ferma da sola a scheda in
      // secondo piano, cosa che un setInterval non fa — ma si dipinge solo quando e'
      // passato l'intervallo. Il primo frame passa sempre (`ultimo` a 0).
      const trascorso = ora - ultimo;
      // `ultimo !== 0` esenta la primissima chiamata, o il canvas resterebbe vuoto per un
      // frame: `draw(0)` cadrebbe sotto l'intervallo e non dipingerebbe niente.
      if (ultimo !== 0 && trascorso < intervallo) return;
      // Passi in unita' di "frame a 60fps", cosi' le velocita' tarate restano quelle e
      // la velocita' apparente non dipende dalla cadenza. Il tetto a 3 evita il salto
      // dopo una pausa lunga (scheda tornata in primo piano).
      const passo = ultimo === 0 ? 1 : Math.min(trascorso / (1000 / 60), 3);
      ultimo = ora;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,180,150,${p.o})`;
        ctx.fill();
        p.x += p.dx * passo;
        p.y += p.dy * passo;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      }
    };

    init();
    draw(0);

    const onResize = () => init();
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        id="ambient-particles"
        className="ambient-particles"
        data-testid="particles-canvas"
        aria-hidden="true"
      />
      <div className="ambient-glow" data-testid="ambient-glow" aria-hidden="true" />
    </>
  );
});

AmbientEffects.displayName = 'AmbientEffects';
